<template>
  <div class="DatePickerComponent">
    <div class="header">
      <label
        v-if="label"
        :for="id"
      >
        {{ label }}
      </label>

      <BaseTooltip
        v-if="tooltip"
        :text="tooltip"
      />
    </div>

    <div class="input">
      <v-text-field
        :id="id"
        ref="$field"
        :aria-describedby="fieldValidationErrorMessage ? id + '-error' : undefined"
        :aria-invalid="!!fieldValidationErrorMessage"
        :hide-details="true"
        :placeholder="placeholder"
        type="text"
        @click="clickDatepicker"
        :rules="validations"
        :append-inner-icon="datePickerVisible ? 'icon-calendar-01' : ''"
        :disabled="disabled"
        @update:focused="openDatePickerOnFocus"
        @input="onInput"
        @keydown.space="$event.preventDefault()"
      />
    </div>

    <p class="hint">
      {{ hint }}
    </p>

    <v-date-picker
      v-if="isDatePickerOpen"
      ref="$datePicker"
      class="v-date-picker"
      color="#1C4A8D"
      role="grid"
      :aria-label="label || ARIA_LABELS.DATE_PICKER"
      v-model="datePicker"
      :month="month"
      :year="year"
      :min="minDate"
      :max="maxDate"
      :disabled="disabled"
      width="100%"
      @keydown="onDatePickerKeydown"
    />

    <ErrorMessageComponent
      v-if="!isDatePickerOpen"
      :messages="fieldValidationErrorMessage"
      :id="id + '-error'"
    />
  </div>
</template>

<script setup lang="ts">
  import ErrorMessageComponent from '@/modules/core/components/formFields/ErrorMessageComponent.vue'
  import { useValidationError } from '@/modules/core/composables/validationErrorsComposable'
  import { ARIA_LABELS } from '@/modules/core/constants/ariaLabels'
  import type { ValidationRuleType } from '@/modules/core/models/types'
  import { ddMMyyyyToDate, format } from '@/utils/date.utils'
  import { validDate } from '@/utils/validations.utils'
  import { computed, nextTick, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '../../composables/accesibilityComposable'
  import BaseTooltip from './BaseTooltip.vue'

  const { t } = useI18n()
  const { $field, fieldValidationErrorMessage } = useValidationError()

  const INPUT_DATE_FORMATTER = new Intl.DateTimeFormat('es', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })

  interface PropsInterface {
    id: string
    label?: string
    hint?: string
    modelValue: Date | null | undefined
    rules?: ValidationRuleType[]
    datePickerVisible?: boolean
    minDate: Date | null
    maxDate: Date | null
    minValidationText?: string
    placeholder?: string
    disabled?: boolean
    tooltip?: string
  }

  const props = withDefaults(defineProps<PropsInterface>(), {
    rules: () => [],
    datePickerVisible: false,
    minDate: null,
    maxDate: null,
    placeholder: 'dd/mm/aaaa',
    disabled: false
  })

  const emit = defineEmits(['update:modelValue'])

  async function validate() {
    return await $field.value?.validate()
  }

  defineExpose({ validate })

  let observer: MutationObserver | null = null

  async function removeDuplicatedLabels() {
    // Esperar render inicial
    await nextTick()
    const rootElement = document.getElementById(props.id || '')?.parentElement as HTMLElement | null
    observer = startDuplicatedLabelControl([rootElement])
  }

  const validationRules = {
    betweenDates: (value: string) => {
      const date = ddMMyyyyToDate(value)
      if (!date) return true
      const currentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      const minValidation = !props.minDate || (props.minDate && currentDate >= props.minDate)
      const maxValidation = !props.maxDate || (props.maxDate && currentDate <= props.maxDate)
      if (!minValidation)
        return props.minValidationText ? props.minValidationText : t('common.validations.date')
      if (!maxValidation) {
        const dateToShow = format(props.maxDate)
        return t('copy_max-date-value', { date: dateToShow })
      }

      return true
    },
    validDate: (value: string) => validDate(value) || t('common.validations.date')
  }

  const validations: ValidationRuleType[] = [
    ...props.rules,
    validationRules.betweenDates,
    validationRules.validDate
  ]

  const datePickerOpen = ref(false)
  const date = ref(convertDateToInputDateFormat(props.modelValue))
  const month = computed(() => {
    const minDate = props.minDate ? props.minDate : new Date()
    return minDate.getMonth()
  })
  const year = computed(() => {
    const minDate = props.minDate ? props.minDate : new Date()
    return minDate.getFullYear()
  })

  const isDatePickerOpen = computed(() => props.datePickerVisible && datePickerOpen.value)

  function applyMask(value: string) {
    let numericValue = value.replace(/\D/g, '')

    const day = numericValue.substring(0, 2).padStart(2, '0')
    const month = numericValue.substring(2, 4).padStart(2, '0')
    const year = numericValue.substring(4, 8).padStart(4, '0')

    numericValue = day + (month ? '/' + month : '') + (year ? '/' + year : '')

    return numericValue.slice(0, 10)
  }

  function removeChar(currentValue: string, cursorPosition: number) {
    let currentValueArr = currentValue.split('')
    currentValueArr[cursorPosition] = '0'
    const newValue = applyMask(currentValueArr.join(''))
    if (newValue[cursorPosition - 1] === '/') {
      cursorPosition--
    }
    return { newValue, cursorPosition }
  }

  function addChar(currentValue: string, newValue: string, cursorPosition: number) {
    let currentValueArr,
      newValueArr = newValue.split('')
    if (currentValue.length) {
      currentValueArr = currentValue.split('')
    } else {
      // input was clear
      currentValueArr = applyMask('').split('')
    }
    const substract = currentValueArr[cursorPosition - 1] === '/' ? 0 : 1
    currentValueArr[cursorPosition - substract] = newValueArr[cursorPosition - 1]

    if (currentValueArr[cursorPosition] === '/' || !substract) {
      cursorPosition += 1
    }
    return { newValue: applyMask(currentValueArr.join('')), cursorPosition }
  }

  function onInput(e: InputEvent) {
    const input = e.target as HTMLInputElement
    const value = input.value
    let resp: { newValue: string; cursorPosition: number }

    let cursorPosition = $field.value.selectionStart
    if (e.inputType.startsWith('deleteContent')) {
      if (!date.value.length) return
      resp = removeChar(date.value, cursorPosition)
    } else {
      resp = addChar(date.value, value, cursorPosition)
    }
    date.value = resp.newValue
    $field.value.value = date.value
    $field.value.setSelectionRange(resp.cursorPosition, resp.cursorPosition)
    $field.value.dispatchEvent(new Event('input'))
  }

  const clickDatepicker = (event: any) => {
    if (navigator.userAgent.indexOf('Safari') > -1) event.preventDefault()
  }

  const datePicker = computed({
    get: () => parseDate(),
    set: (value: Date | null) => {
      date.value = convertDateToInputDateFormat(value)
      updateVDatepicker()
    }
  })

  watch(date, () => {
    emit('update:modelValue', parseDate())
  })

  function parseDate() {
    return ddMMyyyyToDate(date.value)
  }

  function convertDateToInputDateFormat(date: Date | null | undefined) {
    try {
      if (!date || !(date instanceof Date) || date?.toString() === 'Invalid Date') return ''

      const [day, month, year] = INPUT_DATE_FORMATTER.format(date)?.match(/\d+/g) ?? []

      return applyMask(`${day}/${month}/${year}`)
    } catch (error) {
      console.error(error)
      return ''
    }
  }

  function closeDatePickerByClickingOut(e: Event) {
    if (!isDatePickerOpen.value) return

    const isDatePickerClicked = !!(e.target as HTMLElement).closest('.DatePickerComponent')
    if (isDatePickerClicked) return

    datePickerOpen.value = false
  }

  function openDatePickerOnFocus(state: boolean) {
    if (!state) return

    datePickerOpen.value = true
    updateVDatepicker()
  }

  function updateVDatepicker() {
    $field.value.value = date.value
    $field.value.dispatchEvent(new Event('input'))
  }

  const $datePicker = ref<InstanceType<any> | null>(null)

  const DAY_BTN_SELECTOR =
    '.v-date-picker-month__day:not(.v-date-picker-month__day--hide-adjacent):not(.v-date-picker-month__weekday) .v-btn'

  function applyRovingTabindex() {
    nextTick(() => {
      const pickerEl = $datePicker.value?.$el as HTMLElement | undefined
      if (!pickerEl) return

      // Roving tabindex on month navigation arrows
      const monthArrows = Array.from(
        pickerEl.querySelectorAll<HTMLButtonElement>('.v-date-picker-controls__month .v-btn')
      ).filter((btn) => !btn.disabled)
      monthArrows.forEach((btn, i) => (btn.tabIndex = i === 0 ? 0 : -1))

      // Roving tabindex on day buttons
      const dayButtons = pickerEl.querySelectorAll<HTMLButtonElement>(DAY_BTN_SELECTOR)
      if (!dayButtons.length) return

      const selected = pickerEl.querySelector<HTMLButtonElement>(
        '.v-date-picker-month__day .v-btn--active'
      )
      dayButtons.forEach((btn) => (btn.tabIndex = -1))

      if (selected) {
        selected.tabIndex = 0
      } else {
        const firstEnabled = Array.from(dayButtons).find((btn) => !btn.disabled)
        if (firstEnabled) firstEnabled.tabIndex = 0
      }
    })
  }

  function labelEmptyButtons() {
    nextTick(() => {
      const pickerEl = $datePicker.value?.$el as HTMLElement | undefined
      if (!pickerEl) return

      // Prev / Next month arrows
      const monthArrows = pickerEl.querySelectorAll<HTMLButtonElement>(
        '.v-date-picker-controls__month .v-btn'
      )
      monthArrows.forEach((btn, i) => {
        if (!btn.getAttribute('aria-label')) {
          btn.setAttribute(
            'aria-label',
            i === 0 ? t('common.dates.previous-month') : t('common.dates.next-month')
          )
        }
      })

      // Mode button (year selector)
      const modeBtn = pickerEl.querySelector<HTMLButtonElement>('.v-date-picker-controls__mode-btn')
      if (modeBtn && !modeBtn.getAttribute('aria-label')) {
        modeBtn.setAttribute('aria-label', t('common.dates.select-year'))
      }

      // Day buttons: use the day number as aria-label
      const dayButtons = pickerEl.querySelectorAll<HTMLButtonElement>(DAY_BTN_SELECTOR)
      dayButtons.forEach((btn) => {
        if (!btn.getAttribute('aria-label')) {
          const text = btn.textContent?.trim()
          if (text) btn.setAttribute('aria-label', text)
        }
      })
    })
  }

  watch(isDatePickerOpen, (open) => {
    if (open) {
      applyRovingTabindex()
      labelEmptyButtons()
    }
  })

  watch(datePicker, () => {
    applyRovingTabindex()
    labelEmptyButtons()
  })

  function focusDayAfterMonthChange(position: 'first' | 'last') {
    const pickerEl = $datePicker.value?.$el as HTMLElement | undefined
    if (!pickerEl) return

    // Snapshot current buttons to detect when the DOM actually changes
    const oldFirstBtn = pickerEl.querySelector<HTMLButtonElement>(DAY_BTN_SELECTOR)

    function applyFocus() {
      const el = $datePicker.value?.$el as HTMLElement | undefined
      if (!el) return

      const newDayButtons = Array.from(
        el.querySelectorAll<HTMLButtonElement>(DAY_BTN_SELECTOR)
      ).filter((btn) => !btn.disabled)

      if (!newDayButtons.length) return

      // Roving tabindex on arrows
      const monthArrows = Array.from(
        el.querySelectorAll<HTMLButtonElement>('.v-date-picker-controls__month .v-btn')
      ).filter((btn) => !btn.disabled)
      monthArrows.forEach((btn, i) => (btn.tabIndex = i === 0 ? 0 : -1))

      // Focus first or last day
      newDayButtons.forEach((btn) => (btn.tabIndex = -1))
      const target =
        position === 'first' ? newDayButtons[0] : newDayButtons[newDayButtons.length - 1]
      target.tabIndex = 0
      target.focus()
    }

    // Use MutationObserver to wait for Vuetify to finish re-rendering month days
    const mo = new MutationObserver(() => {
      const newFirstBtn = pickerEl.querySelector<HTMLButtonElement>(DAY_BTN_SELECTOR)
      // Wait until the DOM has actually changed (different button nodes = new month)
      if (newFirstBtn && newFirstBtn !== oldFirstBtn) {
        mo.disconnect()
        applyFocus()
        labelEmptyButtons()
      }
    })
    mo.observe(pickerEl, { childList: true, subtree: true })

    // Safety timeout: disconnect observer and force focus after 500ms
    setTimeout(() => {
      mo.disconnect()
      applyFocus()
      labelEmptyButtons()
    }, 500)
  }

  function onDatePickerKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      const btn = document.activeElement as HTMLButtonElement | null
      if (btn?.closest('.v-date-picker-month__day')) {
        e.preventDefault()
        btn.click()
        nextTick(() => {
          datePickerOpen.value = false
          nextTick(() => {
            const container = $field.value?.$el?.closest(
              '.DatePickerComponent'
            ) as HTMLElement | null
            if (!container) return
            const focusableSelector =
              'a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'
            const allFocusable = Array.from(
              document.querySelectorAll<HTMLElement>(focusableSelector)
            )
            const lastInComponent = allFocusable.filter((el) => container.contains(el)).pop()
            if (lastInComponent) {
              const idx = allFocusable.indexOf(lastInComponent)
              const nextEl = allFocusable[idx + 1]
              nextEl?.focus()
            }
          })
        })
      }
      return
    }

    const arrows = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']
    if (!arrows.includes(e.key)) return

    const pickerEl = $datePicker.value?.$el as HTMLElement | undefined
    if (!pickerEl) return

    // Arrow navigation between month prev/next buttons
    const current = document.activeElement as HTMLButtonElement
    if (current?.closest('.v-date-picker-controls__month')) {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault()
        const monthArrows = Array.from(
          pickerEl.querySelectorAll<HTMLButtonElement>('.v-date-picker-controls__month .v-btn')
        ).filter((btn) => !btn.disabled)
        const idx = monthArrows.indexOf(current)
        const nextIdx =
          e.key === 'ArrowRight' ? Math.min(idx + 1, monthArrows.length - 1) : Math.max(idx - 1, 0)
        monthArrows.forEach((btn) => (btn.tabIndex = -1))
        monthArrows[nextIdx].tabIndex = 0
        monthArrows[nextIdx].focus()
      }
      return
    }

    // Arrow navigation on day buttons
    const dayButtons = Array.from(
      pickerEl.querySelectorAll<HTMLButtonElement>(DAY_BTN_SELECTOR)
    ).filter((btn) => !btn.disabled)

    const currentIndex = dayButtons.indexOf(current)
    if (currentIndex === -1) return

    e.preventDefault()
    let nextIndex = currentIndex

    if (e.key === 'ArrowRight') nextIndex = currentIndex + 1
    else if (e.key === 'ArrowLeft') nextIndex = currentIndex - 1
    else if (e.key === 'ArrowDown') nextIndex = currentIndex + 7
    else if (e.key === 'ArrowUp') nextIndex = currentIndex - 7

    // Cross-month navigation: go to next/previous month when out of bounds
    if (nextIndex > dayButtons.length - 1) {
      const nextBtn = pickerEl.querySelector<HTMLButtonElement>(
        '.v-date-picker-controls__month .v-btn:last-child'
      )
      if (nextBtn && !nextBtn.disabled) {
        nextBtn.click()
        focusDayAfterMonthChange('first')
      }
      return
    }

    if (nextIndex < 0) {
      const prevBtn = pickerEl.querySelector<HTMLButtonElement>(
        '.v-date-picker-controls__month .v-btn:first-child'
      )
      if (prevBtn && !prevBtn.disabled) {
        prevBtn.click()
        focusDayAfterMonthChange('last')
      }
      return
    }

    dayButtons.forEach((btn) => (btn.tabIndex = -1))
    dayButtons[nextIndex].tabIndex = 0
    dayButtons[nextIndex].focus()
  }

  onMounted(() => {
    document.addEventListener('click', closeDatePickerByClickingOut)
    if (date.value) updateVDatepicker()
    removeDuplicatedLabels()

    // Label the calendar icon button inside the v-text-field
    nextTick(() => {
      const inputEl = document.getElementById(props.id)
      const wrapper = inputEl?.closest('.v-input') as HTMLElement | null
      const iconBtn = wrapper?.querySelector<HTMLButtonElement>('.v-field__append-inner .v-icon')
      const btnParent = iconBtn?.closest('button') || (iconBtn?.parentElement as HTMLElement | null)
      if (btnParent && !btnParent.getAttribute('aria-label')) {
        btnParent.setAttribute('aria-label', t('common.dates.open-calendar'))
      }
    })
  })

  onBeforeUnmount(() => {
    endDuplicatedLabelControl(observer)
  })
  onUnmounted(() => document.addEventListener('click', closeDatePickerByClickingOut))
</script>

<style lang="scss" scoped>
  .DatePickerComponent {
    label {
      font-family: $font-family-primary;
      font-size: $font-size-m;
      font-style: normal;
      font-weight: $font-weight-400;
      line-height: $line-height-100p;
      color: $color-neutral-dark;
      margin-top: $spacing-s;
      margin-bottom: $spacing-s;
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: $spacing-s;

      label {
        margin-right: $spacing-l;
      }
    }

    .input {
      position: relative;
      overflow-x: clip;
      overflow-clip-margin: 5px;
      width: $size-full;
      background-color: $color-grey-200;

      &:focus-within {
        box-shadow: 0 0 0 4px $color-focus;
      }

      &::placeholder {
        color: $color-neutral;
        opacity: 1;
      }
    }

    .hint {
      font-size: $font-size-s;
      color: $color-neutral;
    }

    :deep(.v-field) {
      border-radius: $b-radius-0;
      box-shadow: $box-shadow-0;
      transition: all 0.2s ease-in-out;

      input {
        font-family: $font-family-primary;
        margin-right: $spacing-negative-100;

        &:focus,
        &:focus-visible {
          outline: none;
          box-shadow: none;
        }
      }

      .v-field__append-inner {
        i {
          cursor: pointer;
          opacity: 1;

          &::before {
            font-family: $font-family-icons;
          }
        }
      }

      .v-field__input {
        font-family: $font-family-primary;
        color: $color-neutral-dark;

        &::placeholder {
          color: $color-neutral;
          opacity: 1;
        }
      }

      /*     &:focus-within {
      box-shadow: $box-shadow-focus;
    } */
    }

    .v-input {
      &--error {
        color: $color-error;

        :deep(.v-input__control) {
          background-color: $color-error-200;

          .v-field__input {
            color: $color-error;
          }
        }

        :deep(.v-field) {
          box-shadow: inset 0 0 0 4px $color-error-light;
        }
      }

      :deep(.v-field) {
        --v-input-control-height: 4rem;
      }
    }

    :deep(.v-date-picker) {
      .v-picker__body {
        @include gl-mx-animation-appear-from-top;
      }

      width: $size-full;

      .text-medium-emphasis {
        padding: 0;
      }

      .v-picker-title,
      .v-picker__header {
        display: none !important;
      }

      .v-date-picker-controls {
        .v-date-picker-controls__month-btn {
          font-size: $font-size-l;
          pointer-events: none;
          cursor: default;
        }

        .v-btn__content {
          display: block;
          font-weight: bold;
          text-transform: none;

          &::first-letter {
            text-transform: uppercase;
          }
        }

        .v-btn--icon.v-btn--density-comfortable {
          display: none;
        }
      }

      .v-date-picker-month {
        padding: 0;

        .v-btn--icon {
          border-radius: 0%;
          min-width: 0;
          padding: 0;
        }

        .v-date-picker-month__days {
          gap: $gap-0;
          justify-content: center;

          .v-date-picker-month__day {
            border-bottom: 0;
            background-color: $color-grey-200;
            width: $size-45;
            height: $size-45;

            &.v-date-picker-month__day--week-start {
              border-left: 0;
            }

            &.v-date-picker-month__day--week-end {
              border-right: 0;
            }

            &.v-date-picker-month__day--hide-adjacent {
              opacity: 0;
            }

            &.v-date-picker-month__weekday {
              border-bottom: 0;
              background-color: transparent;
            }

            .v-btn {
              --v-btn-height: 2.188rem;
              --v-btn-size: 0.85rem;

              border: 0.5px solid $color-grey-200;
            }

            .v-btn--icon.v-btn--density-default {
              width: $size-full;
              height: $size-full;
            }
          }
        }
      }
    }

    // * hide browser calendar icon on Chrome
    :deep(input[type='date']::-webkit-calendar-picker-indicator) {
      display: none;
      appearance: none;
    }

    // * hide calendar icon on other browsers
    // :deep(.v-input__control) {
    // .v-field--error {
    //   background-color: $color-error-200;

    //   .v-field__overlay {
    //     background: transparent !important;
    //   }
    // }

    // .v-field {
    //   input {
    //     margin-right: -35px;
    //   }

    // .v-field__field {
    //   position: relative;
    //   &:after {
    //     content: '';
    //     background: #f5f7fa;
    //     position: absolute;
    //     border-radius: 5px;
    //     padding: $spacing-10;
    //     right: -26px;
    //     top: 18px;
    //     bottom: 18px;
    //     transition: 0.15s background cubic-bezier(0.4, 0, 0.2, 1);
    //   }
    // }
    // &:hover .v-field__field:after {
    //   background-color: #edf1f6;
    // }
    // &.v-field--focused .v-field__field:after {
    //   background-color: #dbe2ed;
    // }
    // &.v-field--error .v-field__field:after {
    //   background-color: $color-error-200;
    //   transition: none;
    // }
    // }
    // }
  }
</style>
